import React, { useCallback, useEffect, useState } from "react";
import api, { portalJob } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { fmtINR } from "@/lib/format";
import { toast } from "sonner";
import { SearchCheck, FileDown, CheckCircle2, XCircle, Image as ImageIcon } from "lucide-react";

const errMsg = (e) => { const d = e?.response?.data?.detail; return typeof d === "string" ? d : d?.message || "gst.gov.in did not respond"; };
const tax = (x) => (x.igst || 0) + (x.cgst || 0) + (x.sgst || 0) + (x.cess || 0);

function useBlobUrl(path) {
  const [url, setUrl] = useState("");
  useEffect(() => {
    let u = "";
    if (!path) { setUrl(""); return undefined; }
    api.get(path, { responseType: "blob" }).then((r) => { u = URL.createObjectURL(r.data); setUrl(u); }).catch(() => setUrl(""));
    return () => { if (u) URL.revokeObjectURL(u); };
  }, [path]);
  return url;
}

function SectionTable({ rows }) {
  return (
    <table className="w-full dense text-xs" data-testid="g1-check-table">
      <thead><tr><th>Section</th><th className="text-right">ERP count</th><th className="text-right">Portal count</th><th className="text-right">ERP taxable</th><th className="text-right">Portal taxable</th><th className="text-right">ERP tax</th><th className="text-right">Portal tax</th><th>Status</th></tr></thead>
      <tbody>{rows.map((r) => (
        <tr key={r.section} className={r.match ? "" : "bg-red-50"} data-testid={`g1-check-row-${r.section}`}>
          <td>{r.label}</td>
          <td className="text-right">{r.erp.count}</td><td className="text-right">{r.count_compared ? r.portal.count : `${r.portal.count}*`}</td>
          <td className="text-right tabular-nums">{fmtINR(r.erp.txval)}</td><td className="text-right tabular-nums">{fmtINR(r.portal.txval)}</td>
          <td className="text-right tabular-nums">{fmtINR(tax(r.erp))}</td><td className="text-right tabular-nums">{fmtINR(tax(r.portal))}</td>
          <td>{r.match ? <span className="text-emerald-700 inline-flex items-center gap-1"><CheckCircle2 className="w-3.5 h-3.5" /> Matches</span>
            : <span className="text-red-700 inline-flex items-center gap-1"><XCircle className="w-3.5 h-3.5" /> Differs{r.diff_txval ? ` · taxable ${fmtINR(r.diff_txval)}` : ""}</span>}</td>
        </tr>))}</tbody>
    </table>
  );
}

export function Gstr1PortalCheck({ period, loggedIn }) {
  const [chk, setChk] = useState(null);
  const [busy, setBusy] = useState(false);
  const [showShot, setShowShot] = useState(true);
  const load = useCallback(async () => {
    try { setChk((await api.get(`/gst-portal/gstr1/${period}/portal-check`)).data); } catch { setChk(null); }
  }, [period]);
  useEffect(() => { load(); }, [load]);
  const shot = useBlobUrl(chk?.has_screenshot ? `/gst-portal/gstr1/checks/${chk.id}/screenshot` : "");

  const run = async () => {
    setBusy(true);
    try { await portalJob(`/gst-portal/gstr1/${period}/portal-check`); toast.success("Portal draft checked"); await load(); }
    catch (e) { toast.error(errMsg(e), { duration: 12000 }); }
    setBusy(false);
  };
  const pdf = async () => {
    try {
      const r = await api.get(`/gst-portal/gstr1/checks/${chk.id}/pdf`, { responseType: "blob" });
      const a = document.createElement("a"); a.href = URL.createObjectURL(r.data); a.download = `GSTR1-portal-draft-${period}.pdf`; a.click();
    } catch (e) { toast.error("PDF not available"); }
  };

  return (
    <div className="border rounded-sm p-3 space-y-3" data-testid="g1-portal-check">
      <div className="flex flex-wrap items-center gap-2">
        <div className="text-sm font-medium">Draft on gst.gov.in</div>
        <div className="flex-1" />
        <Button size="sm" variant="outline" disabled={!loggedIn || busy} onClick={run} className="gap-1.5" data-testid="g1-check-run">
          <SearchCheck className="w-3.5 h-3.5" /> {busy ? "Reading the portal (2–5 min)…" : "Check draft on portal"}</Button>
        {chk?.has_pdf && <Button size="sm" variant="outline" onClick={pdf} className="gap-1.5" data-testid="g1-check-pdf"><FileDown className="w-3.5 h-3.5" /> Portal draft PDF</Button>}
      </div>
      {!chk?.id && <div className="text-xs text-muted-foreground" data-testid="g1-check-empty">Not checked yet. After saving, this generates the summary on gst.gov.in, compares every section with the ERP, saves the portal's draft PDF and a screenshot of the GSTR-1 tiles.</div>}
      {chk?.id && <>
        <div className={`text-xs ${chk.all_match ? "text-emerald-700" : "text-red-700"}`} data-testid="g1-check-status">
          {chk.all_match ? "Every section on the portal matches the ERP." : "Some sections differ — see the red rows."} Checked {new Date(chk.at).toLocaleString("en-IN")} by {chk.by}
          {chk.portal_status ? ` · portal status ${chk.portal_status}` : ""}{chk.stale_possible ? " · the portal may not have refreshed its summary yet, check again in a minute" : ""}
        </div>
        {chk.summary_error && <div className="text-xs border border-amber-300 bg-amber-50 rounded-sm p-2" data-testid="g1-check-summary-error">Portal summary not available: {chk.summary_error}</div>}
        {(chk.notes || []).map((n) => <div key={n} className="text-xs border border-amber-300 bg-amber-50 rounded-sm p-2" data-testid="g1-check-note">{n}</div>)}
        <SectionTable rows={chk.sections || []} />
        <div className="text-[11px] text-muted-foreground">* For B2C (Others) and HSN the portal counts rate/state or HSN lines, not invoices — only amounts are compared.{chk.pdf_error ? ` Draft PDF: ${chk.pdf_error}` : ""}</div>
        {shot && <div className="space-y-1">
          <button type="button" className="text-xs inline-flex items-center gap-1 text-accent" onClick={() => setShowShot(!showShot)} data-testid="g1-check-shot-toggle"><ImageIcon className="w-3.5 h-3.5" /> {showShot ? "Hide" : "Show"} gst.gov.in screenshot</button>
          {showShot && <a href={shot} target="_blank" rel="noreferrer"><img src={shot} alt="GSTR-1 tiles on gst.gov.in" className="border rounded-sm max-h-[520px] w-auto" data-testid="g1-check-shot" /></a>}
        </div>}
      </>}
    </div>
  );
}
